import { createElement, lazy, Suspense, useState, type ComponentType } from 'react';
import { preloadTutorImages } from '@/entities/tutor';
import { DictionariesPageSkeleton, MyListsManagePageSkeleton } from '@/pages/dictionaries/skeleton';
import { HistoryPageSkeleton } from '@/pages/history/skeleton';
import { IdiomDetailPageSkeleton } from '@/pages/idiom/skeleton';
import { MemoryPageSkeleton } from '@/pages/memory/skeleton';
import { MistakesPageSkeleton } from '@/pages/mistakes/skeleton';
import {
  PersonalizationGoalPageSkeleton,
  PersonalizationInterestsPageSkeleton,
  PersonalizationPageSkeleton,
} from '@/pages/personalization/skeleton';
import { RoleplayDetailPageSkeleton } from '@/pages/roleplay/skeleton';
import { SettingsPageSkeleton } from '@/pages/settings/skeleton';
import { TutorSelectPageSkeleton } from '@/pages/tutor-select/skeleton';
import { StatisticsPageSkeleton } from '@/pages/statistics/skeleton';
import { PaymentDetailsPageSkeleton, SubscriptionPageSkeleton } from '@/pages/subscription/skeleton';
import { IdiomAllPageSkeleton, RoleplayAllPageSkeleton } from '@/pages/topics/skeleton';

/**
 * Страницы в своих чанках + их предзагрузка в простое (P1-1 в
 * `PERFORMANCE_PLAN.md`).
 *
 * Стартовый экран грузит только своё, а остальные чанки докачиваются сразу
 * после первого рендера главной (`preloadPagesWhenIdle`), по одной группе на
 * каждое окно простоя. К первому тапу они уже в памяти — страница рендерится
 * сразу, без приостановки и лоадера (см. `lazyPage`).
 *
 * Если человек всё же успел раньше, показывается скелет *этой* страницы —
 * тот же, что она сама рисует, пока грузит данные (`pages/<слайс>/skeleton.ts`).
 * Загрузка кода и загрузка данных выглядят одним непрерывным шиммером, а не
 * двумя разными, сменяющими друг друга. Переходы роутера в `startTransition`
 * выключены, иначе React молча держал бы прежний экран, и тап выглядел бы не
 * сработавшим.
 *
 * Скелеты лежат в стартовом чанке, поэтому им нельзя тянуть ничего тяжёлого:
 * только `PageLayout`, `Skeleton` и переводы.
 *
 * Главная и Профиль сюда не входят — они в стартовом чанке (`AppRouter`).
 */

type Loader = () => Promise<unknown>;

interface ModuleLoader<M> {
  (): Promise<M>;
  /** Модуль, если он уже пришёл, — синхронно. */
  loaded: () => M | undefined;
}

/**
 * Загрузчик, который помнит пришедший модуль. Промис один на загрузчик, так
 * что предзагрузка и рендер не качают чанк дважды; после ошибки сети промис
 * сбрасывается, и следующий вызов запрашивает чанк заново.
 */
function moduleLoader<M>(importModule: () => Promise<M>): ModuleLoader<M> {
  let module: M | undefined;
  let pending: Promise<M> | undefined;
  const load = () =>
    (pending ??= importModule().then(
      (result) => (module = result),
      (error: unknown) => {
        pending = undefined;
        throw error;
      },
    ));
  return Object.assign(load, { loaded: () => module });
}

/**
 * Страница из чанка по именованному экспорту.
 *
 * Голый `lazy` здесь не годится: свою фабрику он зовёт только на первом
 * рендере, а `.then` даже у давно выполненного промиса асинхронный — первый
 * переход на страницу всегда приостанавливался, и React показывал скелет
 * (не меньше ~300 мс, так React 19 троттлит показ контента после фолбэка),
 * хотя предзагрузка давно положила чанк в кеш. Поэтому, если модуль уже
 * пришёл, страница рендерится сразу, а `lazy` остаётся только на случай,
 * когда тап обогнал предзагрузку.
 *
 * Путь выбирается один раз на монтирование (`useState`): иначе после прихода
 * чанка любой повторный рендер (смена `?query` или `:id`) сменил бы дерево с
 * `Suspense → lazy` на прямой компонент, и страница перемонтировалась бы,
 * потеряв своё состояние.
 *
 * `skeleton` — экран на время загрузки чанка. Без него — пустой фон: общий
 * шиммер, который тут же сменится другим, моргает сильнее, чем ничего.
 */
function lazyPage<M, K extends keyof M>(
  load: ModuleLoader<M>,
  name: K,
  skeleton?: ComponentType,
): ComponentType {
  const LazyPage = lazy(() => load().then((module) => ({ default: module[name] as ComponentType })));
  function Page() {
    const [module] = useState(load.loaded);
    if (module) return createElement(module[name] as ComponentType);
    return createElement(
      Suspense,
      { fallback: skeleton ? createElement(skeleton) : null },
      createElement(LazyPage),
    );
  }
  Page.displayName = String(name);
  return Page;
}

const loadOnboarding = moduleLoader(() => import('@/pages/onboarding'));
const loadStatistics = moduleLoader(() => import('@/pages/statistics'));
const loadMistakes = moduleLoader(() => import('@/pages/mistakes'));
const loadDictionaries = moduleLoader(() => import('@/pages/dictionaries'));
const loadVocabulary = moduleLoader(() => import('@/pages/vocabulary'));
const loadChat = moduleLoader(() => import('@/pages/chat'));
const loadTutorSelect = moduleLoader(() => import('@/pages/tutor-select'));
const loadRoleplay = moduleLoader(() => import('@/pages/roleplay'));
const loadIdiom = moduleLoader(() => import('@/pages/idiom'));
const loadTopics = moduleLoader(() => import('@/pages/topics'));
const loadHistory = moduleLoader(() => import('@/pages/history'));
const loadSettings = moduleLoader(() => import('@/pages/settings'));
const loadMemory = moduleLoader(() => import('@/pages/memory'));
const loadPersonalization = moduleLoader(() => import('@/pages/personalization'));
const loadSubscription = moduleLoader(() => import('@/pages/subscription'));
const loadLevelCheck = moduleLoader(() => import('@/pages/level-check'));
const loadDocuments = moduleLoader(() => import('@/pages/documents'));
const loadPremium = moduleLoader(() => import('@/pages/premium'));
const loadLogin = moduleLoader(() => import('@/pages/login'));

/** Онбординг грузит ещё и `RootRedirect` — как только `/me` сказал, что он не пройден. */
export const preloadOnboarding = loadOnboarding;

/** Экран входа — гостю веб-версии, как только онбординг открылся (см. `RootRedirect`). */
export const preloadLogin = loadLogin;

// Без скелета: у онбординга, чата и сессии карточек нет своего шиммера
// данных — повторять нечего.
export const OnboardingPage = lazyPage(loadOnboarding, 'OnboardingPage');
export const LearnPage = lazyPage(loadVocabulary, 'LearnPage');
export const ReviewPage = lazyPage(loadVocabulary, 'ReviewPage');
export const ChatPage = lazyPage(loadChat, 'ChatPage');
// Тот же чанк, что у чата: на итоги попадают только из него, он уже загружен.
export const DialogueReportPage = lazyPage(loadChat, 'DialogueReportPage');
// Данных у выбора тьютора нет, но это первый экран после главной кнопки
// «Свободная практика»: пустой фон, пока едет чанк, выглядел зависанием.
export const TutorSelectPage = lazyPage(loadTutorSelect, 'TutorSelectPage', TutorSelectPageSkeleton);
export const LevelCheckPage = lazyPage(loadLevelCheck, 'LevelCheckPage');
// Пейволл сам рисует место под тарифы, пока они едут, — свой скелет не нужен.
export const PremiumPage = lazyPage(loadPremium, 'PremiumPage');
// Данных нет — шиммеру нечего повторять; чанк крошечный и качается с профилем
// и онбордингом (оттуда ведут ссылки на документы).
export const DocumentsPage = lazyPage(loadDocuments, 'DocumentsPage');
export const DocumentPage = lazyPage(loadDocuments, 'DocumentPage');
export const LoginPage = lazyPage(loadLogin, 'LoginPage');

export const StatisticsPage = lazyPage(loadStatistics, 'StatisticsPage', StatisticsPageSkeleton);
export const MistakesPage = lazyPage(loadMistakes, 'MistakesPage', MistakesPageSkeleton);
export const DictionariesPage = lazyPage(
  loadDictionaries,
  'DictionariesPage',
  DictionariesPageSkeleton,
);
export const MyListsManagePage = lazyPage(
  loadDictionaries,
  'MyListsManagePage',
  MyListsManagePageSkeleton,
);
export const RoleplayDetailPage = lazyPage(
  loadRoleplay,
  'RoleplayDetailPage',
  RoleplayDetailPageSkeleton,
);
export const IdiomDetailPage = lazyPage(loadIdiom, 'IdiomDetailPage', IdiomDetailPageSkeleton);
export const RoleplayAllPage = lazyPage(loadTopics, 'RoleplayAllPage', RoleplayAllPageSkeleton);
export const IdiomAllPage = lazyPage(loadTopics, 'IdiomAllPage', IdiomAllPageSkeleton);
export const HistoryPage = lazyPage(loadHistory, 'HistoryPage', HistoryPageSkeleton);
export const SettingsPage = lazyPage(loadSettings, 'SettingsPage', SettingsPageSkeleton);
export const MemoryPage = lazyPage(loadMemory, 'MemoryPage', MemoryPageSkeleton);
export const PersonalizationPage = lazyPage(
  loadPersonalization,
  'PersonalizationPage',
  PersonalizationPageSkeleton,
);
export const PersonalizationInterestsPage = lazyPage(
  loadPersonalization,
  'PersonalizationInterestsPage',
  PersonalizationInterestsPageSkeleton,
);
export const PersonalizationGoalPage = lazyPage(
  loadPersonalization,
  'PersonalizationGoalPage',
  PersonalizationGoalPageSkeleton,
);
export const SubscriptionPage = lazyPage(
  loadSubscription,
  'SubscriptionPage',
  SubscriptionPageSkeleton,
);
export const PaymentDetailsPage = lazyPage(
  loadSubscription,
  'PaymentDetailsPage',
  PaymentDetailsPageSkeleton,
);

/**
 * Порядок предзагрузки — от самого вероятного следующего тапа. Первыми —
 * выбор тьютора и чат: «Свободная практика» — главная кнопка главной, и на
 * медленной сети, пока перед ними качались recharts и словари, тап по ней
 * упирался в ещё не пришедший чанк (2026-10-08). Дальше Статистика и Ошибки
 * (recharts — самый большой кусок, таб виден на главной), потом словари.
 */
const PRELOAD_GROUPS: readonly (readonly Loader[])[] = [
  // Картинки карусели — с её чанком: без них страница открывалась шиммером.
  [loadTutorSelect, loadChat, () => preloadTutorImages('preview')],
  [loadStatistics, loadMistakes],
  [loadDictionaries, loadVocabulary],
  [
    loadRoleplay,
    loadIdiom,
    loadTopics,
    loadHistory,
    loadSettings,
    loadMemory,
    loadPersonalization,
    loadSubscription,
    loadLevelCheck,
    loadDocuments,
    loadPremium,
    loadLogin,
  ],
];

/**
 * Лёгкие подстраницы, куда ведут строки профиля. Общая цепочка простоя ставит
 * их в самый хвост (последняя группа), а профиль — обычно второй экран после
 * запуска: тап по строке, чей чанк ещё в пути, показывает скелет вместо
 * страницы. Чтобы он мелькал реже, с открытием профиля эти чанки качаются
 * сразу, вне очереди.
 * Повторный `import()` того же пути отдаёт тот же промис — дубля не будет.
 */
export function preloadProfileSubpages(): void {
  void Promise.allSettled([
    loadPersonalization(),
    loadMemory(),
    loadSettings(),
    loadSubscription(),
    loadDocuments(),
    loadPremium(),
  ]);
}

/**
 * В WKWebView (iOS) `requestIdleCallback` нет — там просто откладываем на
 * тик. `timeout` не даёт вечно ждать простоя на странице с анимацией.
 */
function whenIdle(run: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 200);
  }
}

let preloadStarted = false;

/** Идемпотентно: второй вызов (перемонтирование каркаса) ничего не делает. */
export function preloadPagesWhenIdle(): void {
  if (preloadStarted) return;
  preloadStarted = true;

  const next = (index: number) => {
    const group = PRELOAD_GROUPS[index];
    if (!group) return;
    whenIdle(() => {
      // Ошибка сети здесь не страшна: загрузчик сбрасывает упавший промис,
      // и при переходе страница запросит чанк заново. Следующую группу
      // грузим в любом случае.
      void Promise.allSettled(group.map((load) => load())).then(() => next(index + 1));
    });
  };
  next(0);
}
